// eslint-disable-next-line no-unused-vars
import React, {useState, useEffect} from 'react';
import styled from 'styled-components';
import {FaKey} from 'react-icons/fa';
import PageContentComponent from '../components/ui/pageContentComponent';
import PageContainerTitleComponent from '../components/ui/container/pageContainerTitleComponent';
import ErrorMessageComponent from '../components/ui/messageErrorComponent';
import {updatePassword, toggleNewsletter} from '../api/axios';
import {useAuth} from '../hooks/useAuth';
import {useNavigate, Navigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';

const SettingsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 500px;
  margin: 20px auto;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 15px;
  margin-bottom: 30px;
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Input = styled.input`
  padding: 10px 10px 10px 40px;
  border: 1px solid #ccc;
  border-radius: 5px;
  width: 100%;
`;

const Label = styled.label`
  font-weight: bold;
  min-width: 150px;
`;

const Icon = styled.div`
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #555;
`;

const Button = styled.button`
  padding: 10px;
  border: none;
  border-radius: 5px;
  background-color: #007bff;
  color: white;
  cursor: pointer;
  text-align: center;
  &:hover {
    background-color: #0056b3;
  }
`;

const SwitchContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 15px;
`;

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const Switch = styled.input`
  appearance: none;
  max-width: 35px !important;
  height: 20px;
  background: ${({disabled}) => (disabled ? '#999' : '#ccc')} !important;
  border-radius: 10px !important;
  position: relative;
  cursor: ${({disabled}) => (disabled ? 'not-allowed' : 'pointer')};
  transition: background 0.3s;
  padding: 0px 12% !important;

  &:checked {
    background: ${({disabled}) => (disabled ? '#666' : '#007bff')} !important;
  }

  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    background: white;
    border-radius: 50%;
    transition: transform 0.3s;
  }

  &:checked::before {
    transform: translateX(20px);
  }
`;

const SettingsPage = () => {
  const {t} = useTranslation();
  const {user, logout} = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newsletter, setNewsletter] = useState(false);
  const [message, setMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  const [isMuted, setIsMuted] = useState(() => {
    const stored = localStorage.getItem('luckyWheelMuted');
    return stored ? JSON.parse(stored) : false;
  });

  useEffect(() => {
    if (user) {
      setNewsletter(user.newsletter === 1);
    }
  }, [user]);

  const handlePasswordChange = async e => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setMessage(t('settingsPage.passwordMismatch'));
      return;
    }
    try {
      const response = await updatePassword(currentPassword, newPassword);
      setMessage(response.data?.message || t('settingsPage.passwordUpdateSuccess'));
      setTimeout(() => {
        logout();
        navigate('/');
      }, 2000);
    } catch (error) {
      setMessage(error.response?.data?.message || t('settingsPage.passwordUpdateError'));
    }
  };

  const handleNewsletterChange = async () => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      const response = await toggleNewsletter(!newsletter);
      if (response.data.success) {
        setNewsletter(!newsletter);
        setMessage(
          response.data?.message ||
            t('settingsPage.newsletterToggleSuccess', {status: !newsletter ? 'enabled' : 'disabled'})
        );
      }
    } catch (error) {
      setMessage(error.response?.data?.message || t('settingsPage.newsletterToggleError'));
    }
    setTimeout(() => setIsProcessing(false), 3000);
  };

  useEffect(() => {
    localStorage.setItem('luckyWheelMuted', JSON.stringify(isMuted));
  }, [isMuted]);

  const toggleMute = () => {
    setIsMuted(prev => !prev);
  };

  if (!user) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageContentComponent>
      <PageContainerTitleComponent title={t('settingsPage.pageTitle')} />
      <SettingsContainer>
        <ErrorMessageComponent message={message} />
        <Form onSubmit={handlePasswordChange}>
          <Label>{t('settingsPage.changePassword')}</Label>
          <InputWrapper>
            <Icon>
              <FaKey />
            </Icon>
            <Input
              type="password"
              placeholder={t('settingsPage.currentPasswordPlaceholder')}
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              required
              autoComplete="off"
            />
          </InputWrapper>
          <InputWrapper>
            <Icon>
              <FaKey />
            </Icon>
            <Input
              type="password"
              placeholder={t('settingsPage.newPasswordPlaceholder')}
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              required
              autoComplete="off"
            />
          </InputWrapper>
          <InputWrapper>
            <Icon>
              <FaKey />
            </Icon>
            <Input
              type="password"
              placeholder={t('settingsPage.confirmNewPasswordPlaceholder')}
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
              autoComplete="off"
            />
          </InputWrapper>
          <Button type="submit">{t('settingsPage.updatePasswordButton')}</Button>
        </Form>
        <SwitchContainer>
          <SwitchWrapper>
            <Label>{t('settingsPage.newsletterSubscribe')}</Label>
            <Switch type="checkbox" checked={newsletter} onChange={handleNewsletterChange} disabled={isProcessing} />
          </SwitchWrapper>
          <SwitchWrapper>
            <Label>{t('settingsPage.muteLuckyWheelSound')}</Label>
            <Switch type="checkbox" checked={isMuted} onChange={toggleMute} disabled={isProcessing} />
          </SwitchWrapper>
        </SwitchContainer>
      </SettingsContainer>
    </PageContentComponent>
  );
};

export default SettingsPage;
